import React, { useState, useEffect } from "react";
import MainLayout from "../layouts/MainLayout";
import Swal from "sweetalert2";
import { useParams, useNavigate } from "react-router-dom";
import FolderDetailsTable from "../components/FolderDetailsTable";
import Pagination from "../components/Pagination";
import { useLoading } from "../context/LoadingContext";
import { Modal } from "bootstrap";
import useApi from "../utils/useApi";
import Toaster, { ToastContainerComponent } from "../components/Toaster"; // ✅ Toaster for notifications
import { useAuth } from "../context/AuthContext";
import Breadcrumbs from "../components/Breadcrumbs";

import { downloadFileApi, generateFileTokenApi, generateClientFileTokenApi } from "../utils/api";
import InfoModal from "../components/InfoModal";

const FolderDetails = () => {
  const { callApi } = useApi();
  const { setLoading } = useLoading();
  const [foldersData, setFoldersData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderDetails, setNewFolderDetails] = useState("");

  const [searchUserQuery, setSearchUserQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [tempSelectedUsers, setTempSelectedUsers] = useState([]); // Temp list for modal

  const foldersPerPage = parseInt(process.env.REACT_APP_PER_PAGE) || 50; // Default: 5 items per page
  const [totalFoldersData, settotalFoldersData] = useState(0);
  const [folderDetails, setFolderDetails] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewModal, setPreviewModal] = useState(false);
  const [previewFileUrl, setPreviewFileUrl] = useState(null);

  const navigate = useNavigate();
  const [selectedFolderDetails, setSelectedFolderDetails] = useState(null);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const { "*": dynamicPath } = useParams();
  const pathSegments = dynamicPath ? dynamicPath.split("/") : [];
  const entity_id = pathSegments[pathSegments.length - 1]; // Last segment is the folder ID

  const { user } = useAuth();

  const fetchFoldersData = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        length: foldersPerPage,
        sort: "desc",
        entity_id: entity_id,
        keyword: searchQuery || undefined, //  search
      };
      // const response = await callApi({ endpoint: "/admin/directories/get-sub-directory", params });
      const isClient = user?.user_role?.toLowerCase() === "client"; // ✅ Role check
      const endpoint = isClient
        ? "/client/get-sub-directory" // ✅ Client-specific endpoint
        : "/admin/directories/get-sub-directory"; // ✅ Admin endpoint

      // console.log("🔍 User role:", user?.user_role);
      // console.log("🚀 Endpoint called from fetchFoldersData..", endpoint);

      const response = await callApi({ endpoint, params });
      //console.log("response from fetchFoldersData", response?.data);

      if (response?.success) {
        setFoldersData(response.data.directories || []);
        settotalFoldersData(response.data.total || 0);
      } else {
        Toaster.error("Failed to load folder details."); // ✅ Using Toaster for errors
      }
    } catch (error) {
      console.error("Error fetching folder details:", error);
      Toaster.error("Error fetching folder details. Please try again.");
      window.location.href = '/dashboard';
    } finally {
      setLoading(false);
    }
  };

  // useEffect(() => {
  //   fetchFoldersData();
  // }, [currentPage, dynamicPath]);

  const fetchFolderDetails = async () => {
    setLoading(true);
    try {
      // const response = await callApi({ endpoint: `/admin/directories/get-directory-info/${entity_id}` });
      const isClient = user?.user_role?.toLowerCase() === "client";
      const endpoint = isClient
        ? `/client/get-directory-info/${entity_id}`
        : `/admin/directories/get-directory-info/${entity_id}`;

      // console.log("🔍 User role:", user?.user_role);
      // console.log("🚀 Endpoint called 2:", endpoint);

      const response = await callApi({ endpoint });

      if (response?.success) {
        const data = response.data || {};

        //console.log("📂 Folder details fetched:", data);

        // ✅ Fix permission handling
        const hasAccess =
          !isClient ||
          data?.directory_access?.includes(user.user_id) ||
          data?.asigned_user_ids?.includes(user.user_id);

        if (hasAccess) {
          setFolderDetails(data);
          //setSelectedFiles(data.child_files_ids || []);
          setSelectedFiles([]);
        } else {
          Toaster.error("You don't have permission to view this folder.");
          setFolderDetails(null);
          setSelectedFiles([]);
        }
        setFolderDetails(response.data); // ✅ Store the entire folder details
        setSelectedFiles([]);
      } else {
        Toaster.error("Failed to load folder details.");
      }
    } catch (error) {
      console.error("Error fetching folder details:", error);
      Toaster.error("Error fetching folder details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (entity_id) {
      fetchFolderDetails(); // ✅ Call API when folder ID changes
    }
    fetchFoldersData();
  }, [entity_id, currentPage, dynamicPath, searchQuery]);

  // ✅ Handle Folder Creation
  const createFolder = async () => {
    if (!newFolderName.trim()) {
      Toaster.error("Folder name is required."); // ✅ Toaster instead of alert
      return;
    }

    setLoading(true);
    try {
      const response = await callApi({
        endpoint: "/admin/directories/create-directory",
        method: "POST",
        data: {
          entity_name: newFolderName,
          entity_description: newFolderDetails,
          parent_directory_id: entity_id,
          directory_owner: user.user_id,
        },
      });

      if (response?.success) {
        const entityId = response.data.entity_id;
        const usersToAssign = folderDetails?.asigned_user_ids || [];

        // Reset form state
        setNewFolderName("");
        setNewFolderDetails("");

        // Force-close modal: remove all Bootstrap modal DOM state directly
        const modalElement = document.getElementById("createFolder");
        if (modalElement) {
          const existingInstance = Modal.getInstance(modalElement);
          if (existingInstance) existingInstance.dispose();

          modalElement.classList.remove("show");
          modalElement.style.display = "none";
          modalElement.removeAttribute("aria-modal");
          modalElement.setAttribute("aria-hidden", "true");
        }
        document.body.classList.remove("modal-open");
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
        document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());

        // Now safe to fetch & show toast
        Toaster.success("Folder created successfully!");
        fetchFoldersData();
        if (usersToAssign.length > 0) {
          assignFolderTouser(usersToAssign, entityId);
        }
      } else {
        Toaster.error(response?.data?.message || "Failed to create folder.");
      }
    } catch (error) {
      console.error("Error creating folder:", error);
      Toaster.error("Error creating folder. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const detailsFolder = async (entity_id, file_type, entity_location) => {
    // console.log("Entity ID:", entity_id);
    // console.log("File Type:", file_type);
    // console.log("Entity Location:", entity_location);
    // console.log("Backend URL Root:", process.env.REACT_APP_BACKEND_FILE_URL_ROOT);

    if (entity_location.startsWith("../")) {
      entity_location = entity_location.replace(/^(\.\.\/)+/, ""); // Remove all leading ../
    }

    if (file_type === "") {
      //console.log(`Navigating to /folder-details/${dynamicPath}/${entity_id}`);
      navigate(`/folder-details/${dynamicPath}/${entity_id}`);
    } else {
      try {
        setLoading(true);
        
        // Generate secure one-time file token
        const isClient = user?.user_role?.toLowerCase() === 'client';
        const tokenResponse = isClient 
          ? await generateClientFileTokenApi(entity_location, false)
          : await generateFileTokenApi(entity_location, false);
        
        if (tokenResponse?.data?.success) {
          const fileToken = tokenResponse.data.data.token;
          
          // Build URL with secure file token AND JWT token for iframe authentication
          let fileUrl = `${process.env.REACT_APP_BACKEND_FILE_URL_ROOT}${entity_location}`;
          fileUrl = fileUrl.replace(/["]/g, "");
          
          // Get JWT token from localStorage (iframe cannot send Authorization header)
          const jwtToken = localStorage.getItem("accessToken");
          if (jwtToken) {
            fileUrl += `?token=${encodeURIComponent(jwtToken)}`;
            fileUrl += `&fileToken=${encodeURIComponent(fileToken)}`;
          } else {
            fileUrl += `?fileToken=${encodeURIComponent(fileToken)}`;
          }
          
          setPreviewModal(true);
          setPreviewFileUrl(fileUrl);
          
          console.log('Secure file URL generated (token expires in 5 minutes)');
        } else {
          Toaster.error('Failed to generate secure file access');
        }
      } catch (error) {
        console.error('Error generating file token:', error);
        Toaster.error('Failed to access file');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDownloadFile = async () => {
    if (!previewFileUrl) return;

    try {
      setLoading(true);

      // Add download=1 query parameter to force download
      const downloadUrl = previewFileUrl + (previewFileUrl.includes('?') ? '&' : '?') + 'download=1';

      // Use a temporary <a> element — no memory/DOM leak, no 5s timer
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      Toaster.success("File download started!");
    } catch (error) {
      console.error("Error downloading file:", error);
      Toaster.error("Failed to download file. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Direct download from table row without opening preview
// Direct download from table row WITHOUT CORS (no fetch), no new tab
// Direct download without CORS, no new tab, no SPA URL change

const downloadFileDirectly = async (entity_location) => {

  if (!entity_location) return;
 
  try {

    setLoading(true);
 
    // Clean up the path

    let cleanedLocation = entity_location.replace(/^\.\.\//, "");

    cleanedLocation = cleanedLocation.replace(/\/+$/, "");
 
    // Generate secure one-time download token
    const isClient = user?.user_role?.toLowerCase() === 'client';
    const tokenResponse = isClient 
      ? await generateClientFileTokenApi(cleanedLocation, true)
      : await generateFileTokenApi(cleanedLocation, true);
    
    if (!tokenResponse?.data?.success) {
      Toaster.error('Failed to generate secure download link');
      return;
    }
    
    const fileToken = tokenResponse.data.data.token;
    
    // Build the file URL

    const backendUrl = process.env.REACT_APP_BACKEND_FILE_URL_ROOT;

    const encodedLocation = cleanedLocation

      .split("/")

      .map((segment) => encodeURIComponent(segment))

      .join("/");
 
    let fileUrl = backendUrl + encodedLocation;
 
    // Get JWT token for authentication (required by authentication middleware)
    const jwtToken = localStorage.getItem("accessToken");
    
    // Add JWT token, secure file token, and download flag
    if (jwtToken) {
      const downloadUrl = fileUrl + `?token=${encodeURIComponent(jwtToken)}&fileToken=${encodeURIComponent(fileToken)}&download=1`;
      
      // IMPORTANT: no fetch() => no CORS

      const a = document.createElement("a");

      a.href = downloadUrl;

      a.setAttribute("download", ""); // hint only; server controls filename

      a.style.display = "none";
 
      document.body.appendChild(a);

      a.click();

      document.body.removeChild(a);
 
      Toaster.success("Download started!");
    } else {
      Toaster.error("Authentication token not found");
    }

  } catch (error) {

    console.error("Error downloading file:", error);

    Toaster.error("Failed to start download. Please try again.");

  } finally {

    setLoading(false);

  }

};

 
 

  const openCreateFolderModal = () => {
    const modalElement = document.getElementById("createFolder");
    if (modalElement) {
      const modalInstance =
        Modal.getInstance(modalElement) || new Modal(modalElement);
      modalInstance.show(); // ✅ Force show modal
    }
  };

  const openCreateFileModal = () => {
    const modalElement = document.getElementById("createFile");
    if (modalElement) {
      const modalInstance =
        Modal.getInstance(modalElement) || new Modal(modalElement);
      modalInstance.show(); // ✅ Force show modal
    }
  };

  const openAssignedPeopleModal = () => {
    const modalElement = document.getElementById("peoplesFolder");
    if (modalElement) {
      const modalInstance =
        Modal.getInstance(modalElement) || new Modal(modalElement);
      modalInstance.show(); // ✅ Force show modal
    }
  };

  // ✅ Handle File Selection
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 20) {
      Toaster.error("You can upload a maximum of 20 files at once.");
      e.target.value = ""; // reset the input
      return;
    }
    const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MB
    const oversizedFiles = files.filter((file) => file.size > MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      Toaster.error(
        `The following file(s) exceed the 20 MB limit: ${oversizedFiles.map((f) => f.name).join(", ")}`
      );
      e.target.value = "";
      return;
    }
    setSelectedFiles(files);
  };

  // ✅ Handle File Upload
  const createFile = async () => {
    if (selectedFiles.length === 0) {
      Toaster.error("Please select at least one file.");
      return;
    }
    if (selectedFiles.length > 20) {
      Toaster.error("You can upload a maximum of 20 files at once.");
      return;
    }
    //Validate single extension
    const invalidFiles = selectedFiles.filter(file => {
      const parts = file.name.split(".");
      return parts.length !== 2; // allow only one dot (e.g., name.pdf)
    });

    if (invalidFiles.length > 0) {
      Toaster.error("Only files with a single extension are allowed (e.g., demo.pdf).");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => {
        formData.append("files", file); // Append each file with the same field name
      });
      formData.append("parent_directory_id", entity_id);
      formData.append("directory_owner", user.user_id);

      const response = await callApi({
        endpoint: "/admin/directories/create-file",
        method: "POST",
        data: formData,
      });

      if (response?.success) {
        // Reset form state
        setSelectedFiles([]);

        // Force-close modal: remove all Bootstrap modal DOM state directly
        const modalElement = document.getElementById("createFile");
        if (modalElement) {
          const existingInstance = Modal.getInstance(modalElement);
          if (existingInstance) existingInstance.dispose();

          modalElement.classList.remove("show");
          modalElement.style.display = "none";
          modalElement.removeAttribute("aria-modal");
          modalElement.setAttribute("aria-hidden", "true");
        }
        document.body.classList.remove("modal-open");
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
        document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());

        // Now safe to fetch & show toast
        Toaster.success("Files uploaded successfully!");
        fetchFoldersData();
      } else {
        Toaster.error(response?.data?.message || "Failed to upload files.");
      }
    } catch (error) {
      console.error("Error uploading files:", error);
      Toaster.error("Error uploading files. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const assignFolderTouser = async (userids, entity_id) => {
    setLoading(true);
    try {
      const data = {
        directory_ids: [entity_id],
        user_ids: userids,
      };
      const response = await callApi({
        endpoint: "/admin/directories/add-directory-asigned-user",
        method: "POST",
        data: data,
      });

      if (response?.success) {
        //console.log("Users assigned to folder");
        fetchFolderDetails();
      } else {
        console.log("Failed to assigned to folder."); // ✅ Using Toaster for errors
      }
    } catch (error) {
      console.error("Error assigned to folder:", error);
    } finally {
      setLoading(false);
    }
  };

  const reassignPeople = async () => {
    if (folderDetails?.assigned_users?.length === 0) {
      // return Swal.fire("No Users Assigned", "warning");
      const modalElementpeoplesFolder =
        document.getElementById("peoplesFolder");
      const modalInstancemodalElementpeoplesFolder = Modal.getInstance(
        modalElementpeoplesFolder
      );
      modalInstancemodalElementpeoplesFolder.hide();
      openUserModal();
      return false;
    }

    Swal.fire({
      title: `Delete ${folderDetails?.assigned_users?.length} Assigned User(s) from this folder?`,
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, Delete!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading(true);
          await callApi({
            endpoint: "admin/directories/remove-directory-asigned-user",
            method: "POST",
            data: {
              user_ids:
                folderDetails?.assigned_users?.map((user) => user.user_id) ||
                [],
              entity_ids: [folderDetails?.entity_id],
            },
          });

          // Remove deleted users from state
          Swal.fire(
            "Deleted!",
            `${folderDetails?.assigned_users?.length} users have been deleted.`,
            "success"
          );

          fetchFolderDetails();
          const modalElementpeoplesFolder =
            document.getElementById("peoplesFolder");
          const modalInstancemodalElementpeoplesFolder = Modal.getInstance(
            modalElementpeoplesFolder
          );
          modalInstancemodalElementpeoplesFolder.hide();
          openUserModal();
        } catch (error) {
          console.error("Error deleting users:", error);
          Swal.fire(
            "Error",
            "Failed to delete users. Try again later.",
            "error"
          );
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const fetchUsers = async (keyword) => {
    if (keyword.length < 3) {
      setAllUsers([]); // Clear list if query is less than 3 characters
      return;
    }

    setIsSearching(true);
    try {
      const response = await callApi({
        endpoint: "/admin/clients",
        params: { keyword }, // Pass keyword for search
      });

      if (response?.success) {
        setAllUsers(response.data.users || []);
      } else {
        Toaster.error("Failed to load users.");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      Toaster.error("Error fetching users.");
    } finally {
      setIsSearching(false);
    }
  };

  // ✅ Open User Selection Modal
  const openUserModal = () => {
    setTempSelectedUsers([...selectedUsers]); // Copy current selections to temp
    //document.getElementById("peoplesFolder").style.opacity = "0.7";
    const modalElement = document.getElementById("addPeopleModal");
    const modalInstance =
      Modal.getInstance(modalElement) || new Modal(modalElement);
    modalInstance.show();
  };

  // ✅ Handle User Selection (Temporary Selection)
  const toggleTempUserSelection = (user) => {
    setTempSelectedUsers((prevUsers) => {
      return prevUsers.some((u) => u.user_id === user.user_id)
        ? prevUsers.filter((u) => u.user_id !== user.user_id) // Remove user
        : [...prevUsers, user]; // Add user
    });
  };

  const handleAssignUsers = () => {
    setSelectedUsers(tempSelectedUsers); // Update assigned users

    console.log("ssss", tempSelectedUsers?.map((user) => user.user_id) || []);

    assignFolderTouser(
      tempSelectedUsers?.map((user) => user.user_id) || [],
      entity_id
    );
    fetchFolderDetails();
    setSelectedUsers([]);
    setTempSelectedUsers([]);
    setSearchUserQuery("");

    // Force-close modal: remove all Bootstrap modal DOM state directly
    const modalElement = document.getElementById("addPeopleModal");
    if (modalElement) {
      const existingInstance = Modal.getInstance(modalElement);
      if (existingInstance) existingInstance.dispose();

      modalElement.classList.remove("show");
      modalElement.style.display = "none";
      modalElement.removeAttribute("aria-modal");
      modalElement.setAttribute("aria-hidden", "true");
    }
    document.body.classList.remove("modal-open");
    document.body.style.overflow = "";
    document.body.style.paddingRight = "";
    document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
  };

  const toggleFileSelection = (fileId) => {
    setSelectedFiles(
      (prevSelected) =>
        prevSelected.includes(fileId)
          ? prevSelected.filter((id) => id !== fileId) // Remove selection
          : [...prevSelected, fileId] // Add selection
    );
  };

  const toggleSelectFileAll = () => {
    setSelectedFiles(
      selectedFiles.length === foldersData.length && foldersData.length > 0
        ? [] // Deselect all
        : foldersData.map((folder) => folder.entity_id) // Select all
    );
  };

  const deleteSelected = () => {
    if (selectedFiles.length === 0) {
      return Swal.fire("No file or folder selected", "warning");
    }

    Swal.fire({
      title: `Delete`,
      text: "Are you sure want to delete?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, Delete!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading(true);
          await callApi({
            endpoint: "admin/directories/delete-directory",
            method: "Delete",
            data: { entity_ids: selectedFiles },
          });

          // Remove deleted users from state
          Swal.fire("Deleted!", `Data have been deleted.`, "success");

          fetchFolderDetails();
          fetchFoldersData();
        } catch (error) {
          console.error("Error deleting :", error);
          Swal.fire("Error", "Failed to delete. Try again later.", "error");
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const downloadSelected = async () => {
    if (selectedFiles.length === 0) {
      Toaster.error("No files selected for download.");
      return;
    }

    setLoading(true);
    try {
      const path =
        user?.user_role?.toLowerCase() === "client"
          ? "client"
          : "admin/directories";
      const response = await downloadFileApi(selectedFiles, path);
      if (!response) {
        throw new Error("No data received.");
      }

      // ✅ Create a Blob from the response data
      const blob = new Blob([response.data], { type: "application/zip" });

      // ✅ Generate a download URL
      const downloadUrl = window.URL.createObjectURL(blob);

      // ✅ Create a temporary link element
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `SelectedFiles_${new Date().toISOString()}.zip`; // Custom filename

      // ✅ Append link, trigger click, then remove it
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // ✅ Revoke the Blob URL to free memory
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error downloading ZIP:", error);
      Toaster.error("Error downloading files. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const onRename = async (newSubmitFolderName, fDetails) => {
    if (!newSubmitFolderName.trim()) {
      Toaster.error("Folder name is required."); // ✅ Toaster instead of alert
      return;
    }

    setLoading(true);
    try {
      const response = await callApi({
        endpoint: "/admin/directories/rename-folder",
        method: "POST",
        data: {
          entity_name: newSubmitFolderName,
          entity_id: fDetails.entity_id,
        },
      });

      if (response?.success) {
        Toaster.success("Folder renamed successfully!");

        fetchFolderDetails(); // Reload folders
        fetchFoldersData();
        setIsInfoModalOpen(false);
      } else {
        Toaster.error(response?.data?.message || "Failed to rename folder.");
      }
    } catch (error) {
      console.error("Error rename folder:", error);
      Toaster.error("Error rename folder. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getSelectedFolderDetails = async (entity_id) => {
    setLoading(true);
    try {
      //const response = await callApi({ endpoint: `/admin/directories/get-directory-info/${entity_id}` });
      const isClient = user?.user_role?.toLowerCase() === "client";

      // ✅ Use correct endpoint for client/admin
      const endpoint = isClient
        ? `/client/get-directory-info/${entity_id}`
        : `/admin/directories/get-directory-info/${entity_id}`;

      // console.log("🔍 User role:", user?.user_role);
      // console.log("🚀 Endpoint called:", endpoint);

      const response = await callApi({ endpoint });

      if (response?.success) {
        setSelectedFolderDetails(response?.data);
        setIsInfoModalOpen(true);
      } else {
        Toaster.error("Failed to fetch folder details."); // ✅ Using Toaster for errors
      }
    } catch (error) {
      console.error("Failed to fetch folder details:", error);
      Toaster.error("Failed to fetch folder details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const folderDetailsModal = (entity_id) => {
    getSelectedFolderDetails(entity_id);
  };

  const addFolderFavorite = async (entity_id, is_favorite) => {
    if (is_favorite === true) {
      removeFavorite(entity_id);
    } else {
      addFavorite(entity_id);
    }
  };

  const removeFavorite = async (entity_id) => {
    setLoading(true);
    try {
      const response = await callApi({
        endpoint: "/admin/clients/remove-user-favourite-directory",
        method: "POST",
        data: {
          entity_id: entity_id,
        },
      });

      if (response?.success) {
        Toaster.success("Folder removed from favorites!");

        fetchFoldersData();
      } else {
        Toaster.error(
          response?.data?.message || "Failed to remove favorite folder."
        );
      }
    } catch (error) {
      console.error("Error to remove favorite folder:", error);
      Toaster.error("Error to remove favorite folder. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const addFavorite = async (entity_id) => {
    setLoading(true);
    try {
      const response = await callApi({
        endpoint: "/admin/clients/add-user-favourite-directory",
        method: "POST",
        data: {
          entity_id: entity_id,
        },
      });

      if (response?.success) {
        Toaster.success("Folder add to favorite successfully!");

        fetchFoldersData();
      } else {
        Toaster.error(response?.data?.message || "Failed to favorite folder.");
      }
    } catch (error) {
      console.error("Error favorite folder:", error);
      Toaster.error("Error favorite folder. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <ToastContainerComponent /> {/* ✅ Toaster Container for notifications */}
      <div className="inner_full_right_part">
        <div className="row">
          <div className="col-md-12">
            <div className="global_cardbox">
              <div className="global_card_header">
                <div className="row">
                  <div className="col-lg-6">
                    <div className="outer_holder_global_head">
                      {/* <Breadcrumbs folderDetails={folderDetails} setFolderDetails={setFolderDetails} /> */}
                      <Breadcrumbs folderDetails={folderDetails} />
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="heading_block">
                      <input
                        type="text"
                        placeholder="Search"
                        value={searchQuery}
                        onChange={(e) => {
                          setCurrentPage(1);
                          setSearchQuery(e.target.value);
                        }}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {user?.user_role?.toLowerCase() !== "client" && (
                    <div className="col-lg-6">
                      <div className="action_block">
                        <a
                          className="add_folder"
                          href="#!"
                          onClick={openCreateFolderModal}
                        >
                          <span>
                            <i className="fa-solid fa-plus"></i>
                          </span>{" "}
                          Create Folder
                        </a>

                        <a
                          className="add_folder"
                          href="#!"
                          onClick={openCreateFileModal}
                        >
                          <span>
                            <i className="fa-regular fa-file-pdf"></i>
                          </span>{" "}
                          Upload File
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="action_btn_holder">
                {/* <div className="action_btn_holder_inner">
                <button type="button" className="btn btn-primary red_btn"><span><i class="fa-solid fa-trash-can"></i></span><em>Delete Selected</em></button>
                <button type="button" className="btn btn-primary green_btn"><span><i class="fa-solid fa-file-arrow-down"></i></span><em>Download File</em></button>
                </div> */}
                {selectedFiles.length > 0 && (
                  <div className="action_btn_holder_inner">
                    {user?.user_role?.toLowerCase() !== "client" && (
                      <button
                        type="button"
                        className="btn btn-primary red_btn"
                        onClick={deleteSelected}
                      >
                        <span>
                          <i className="fa-solid fa-trash-can"></i>
                        </span>
                        <em>Delete Selected</em>
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-primary green_btn"
                      onClick={downloadSelected}
                    >
                      <span>
                        <i className="fa-solid fa-file-arrow-down"></i>
                      </span>
                      <em>Download Selected</em>
                    </button>
                  </div>
                )}

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={openAssignedPeopleModal}
                >
                  <span>
                    <i className="fa-solid fa-users"></i>
                  </span>
                  <em>People In This Folder</em>
                </button>
              </div>
              <div className="shared_folder_body Personal_table">
                <FolderDetailsTable
                  foldersData={foldersData}
                  detailsFolder={detailsFolder}
                  downloadFileDirectly={downloadFileDirectly}
                  toggleSelectFileAll={toggleSelectFileAll}
                  selectedFiles={selectedFiles}
                  toggleFileSelection={toggleFileSelection}
                  folderDetailsModal={folderDetailsModal}
                  addFolderFavorite={addFolderFavorite}
                />
                <Pagination
                  currentPage={currentPage}
                  totalPages={Math.ceil(totalFoldersData / foldersPerPage)}
                  setCurrentPage={setCurrentPage}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ✅ Create Folder Modal */}
      <div className="modal fade global_modal" tabIndex="-1" id="createFolder">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Create Folder</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <div className="global_modal_body">
                <div className="form-group">
                  <label htmlFor="folderName">Name</label>
                  <input
                    type="text"
                    id="folderName"
                    className="form-control"
                    placeholder="Folder Name"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="folderDetails">Details</label>
                  <textarea
                    id="folderDetails"
                    placeholder="Details"
                    className="form-control"
                    maxLength="1000"
                    value={newFolderDetails}
                    onChange={(e) => setNewFolderDetails(e.target.value)}
                  />
                  <p>Character Limit: 1000</p>
                </div>

                {/* ✅ Buttons */}
                <div className="btn_holder">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={createFolder}
                  >
                    Submit
                  </button>
                  <button
                    data-bs-dismiss="modal"
                    className="btn btn-primary orange_btn"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal fade global_modal" tabIndex="-1" id="createFile">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Upload Files</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <div className="global_modal_body">
                <div className="form-group">
                  <label htmlFor="fileName">Select Files (Max 20 files) *maximum 20 Mb is allowed</label>
                  <input
                    type="file"
                    id="fileName"
                    className="form-control"
                    placeholder="Choose Files"
                    onChange={handleFileChange}
                    multiple
                  />
                </div>

                {/* File Preview Section */}
                {selectedFiles.length > 0 && (
                  <div className="form-group mt-3">
                    <label>Selected Files:</label>
                    <ul className="list-group">
                      {selectedFiles.map((file, index) => (
                        <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                          <span>{file.name}</span>
                          <small className="text-muted">{(file.size / 1024).toFixed(2)} KB</small>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <br />


                {/* Buttons */}
                <div className="btn_holder">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={createFile}
                  >
                    Upload Files
                  </button>
                  <button
                    data-bs-dismiss="modal"
                    className="btn btn-primary orange_btn"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal fade global_modal" tabIndex="-1" id="peoplesFolder">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Assigned To This Folder</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <div className="global_modal_body">
                <ul className="">
                  {folderDetails?.assigned_users?.length === 0 ? (
                    <li>No users available.</li>
                  ) : (
                    // If there are users, loop through and display them
                    folderDetails?.assigned_users?.map((user, index) => (
                      <li
                        className="list-group-item"
                        key={user.user_id || index}
                      >
                        <div className="row">
                          <div className="col-md-2">
                            <i className="fas fa-user-tie"></i>
                          </div>
                          <div className="col-md-8">
                            {user.user_name} {/* Render user's name */}
                          </div>
                        </div>
                      </li>
                    ))
                  )}
                </ul>

                {/* ✅ Buttons */}
                {user?.user_role?.toLowerCase() !== "client" && (
                  <div className="btn_holder">
                    <button
                      type="button"
                      className="btn btn-warning"
                      onClick={reassignPeople}
                    >
                      <i className="fas fa-share"></i> Reassign
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ✅ Add People Modal */}
      <div
        className="modal fade global_modal addpeople_modal"
        id="addPeopleModal"
        tabIndex="-1"
      >
        <div className="modal-dialog">
          <div className="modal-content ">
            <div className="modal-header">
              <h5 className="modal-title">Select Peoples</h5>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="folderName">Search People</label>
                <input
                  type="text"
                  autoComplete="off"
                  placeholder="Search Clients..."
                  value={searchUserQuery}
                  onChange={(e) => {
                    setSearchUserQuery(e.target.value);
                    fetchUsers(e.target.value);
                  }}
                />
                {isSearching && <p>Loading users...</p>}
              </div>

              {/* User Selection */}
              <ul className="search_ppl">
                {allUsers.map((user) => (
                  <li key={user.user_id}>
                    <input
                      type="checkbox"
                      checked={tempSelectedUsers.some(
                        (u) => u.user_id === user.user_id
                      )}
                      onChange={() => toggleTempUserSelection(user)}
                    />
                    {user.user_name}
                  </li>
                ))}
              </ul>

              {/* ✅ Assign Button */}
              <button onClick={handleAssignUsers} className="btn btn-primary">
                Assign
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* ✅ Folder Details Modal */}
      {isInfoModalOpen && (
        <InfoModal
          isOpen={isInfoModalOpen}
          onClose={() => setIsInfoModalOpen(false)}
          folderDetails={selectedFolderDetails}
          onRename={onRename}
        />
      )}
      {previewModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
              {/* <button
                className="btn btn-sm btn-primary"
                onClick={handleDownloadFile}
                style={{ marginRight: "auto" }}
              >
                ⤓ Download
              </button> */}
              <button
                className="close-button"
                onClick={() => setPreviewModal(false)}
              >
                ✖
              </button>
            </div>
            <iframe
              src={previewFileUrl}
              title="PDF Preview"
              width="100%"
              height="600px"
              style={{ border: "none" }}
            />
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default FolderDetails;
